/**
 * Settings > My-Kitchen data layer (Plan 19).
 *
 * Exposes TanStack Query hooks for:
 *   - my-kitchens             — kitchen Orgs the actor has CHEF_* membership at
 *   - kitchen detail (vendor profile + payout summary)
 *   - team roster + pending invites
 *   - vendor profile update
 *   - chef staff invite
 *   - membership remove (with last-admin / self-removal guards server-side)
 *   - kitchen change requests (Plan 27 BlockingTask)
 *
 * Server REST routes live at `/api/kitchen-settings/*` (see
 * `apps/ordo/server/src/routes/kitchen-settings.ts`). The single source
 * of truth for writes is `KitchenSettingsManager`.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { apiFetch } from './http'

// ---------------------------------------------------------------------------
// Types — keep identical to the server response shapes so component code
// reads off the API module without re-mapping.
// ---------------------------------------------------------------------------

export type ChefRole = 'CHEF_ADMIN' | 'CHEF_STAFF' | 'SUPPLIER_ADMIN' | 'SUPPLIER_STAFF'

export const CHEF_ROLES: ReadonlyArray<ChefRole> = ['SUPPLIER_ADMIN', 'SUPPLIER_STAFF', 'CHEF_ADMIN', 'CHEF_STAFF']

export type KitchenChangeRequestUrgency = 'ROUTINE' | 'TIME_SENSITIVE' | 'URGENT'

export interface KitchenOrganizationSummary {
  id: string
  name: string
  slug: string
  type: string
  logoUri: string | null
  description: string | null
  phoneNumber: string | null
  timeZone: string | null
  profileCodes: ChefRole[]
}

export interface MyKitchensResponse {
  me: {
    id: string
    kitchenOrganizations: KitchenOrganizationSummary[]
  } | null
}

export interface KitchenOrganizationDetail {
  id: string
  name: string
  slug: string
  type: string
  logoUri: string | null
  description: string | null
  phoneNumber: string | null
  timeZone: string | null
  /** Schema follow-up — null today. Surfaces as "Not yet provisioned". */
  displayName: string | null
  contactEmail: string | null
  legalName: string | null
  ein: string | null
  businessAddress: string | null
  servedLocations: Array<{ id: string; name: string }>
}

export interface KitchenDetailResponse {
  organization: KitchenOrganizationDetail
}

export interface KitchenTeamUser {
  id: string
  firstName: string
  lastName: string
  email: string | null
  phoneNumber: string | null
  avatarUri: string | null
}

export interface KitchenTeamMembership {
  id: string
  userId: string
  profileCode: ChefRole
  startedAt: string
  user: KitchenTeamUser
}

export interface KitchenPendingInvite {
  id: string
  email: string
  profileCode: ChefRole
  status: string
  createdAt: string
  expiresAt: string
}

export interface KitchenTeamResponse {
  memberships: KitchenTeamMembership[]
  pendingInvitations: KitchenPendingInvite[]
}

export interface KitchenPayoutDestination {
  bankName: string | null
  last4: string | null
  status: 'PROVISIONED' | 'PENDING' | 'NOT_CONFIGURED'
}

export interface KitchenPayoutsResponse {
  payoutDestination: KitchenPayoutDestination
  tipaltiEnabled: boolean
}

/** Server enum for the kitchen-wide reading mode (`ChefMeasurementPreference.displayMode`). */
export type MeasurementDisplayMode = 'STANDARD' | 'TECHNICAL'

export interface KitchenMeasurementPreference {
  organizationId: string
  /** Effective mode — the server falls through to the product default (STANDARD). */
  displayMode: MeasurementDisplayMode
  /** False while the kitchen has never set it explicitly. */
  isExplicit: boolean
}

export interface KitchenMeasurementPreferenceResponse {
  measurementPreference: KitchenMeasurementPreference
}

export interface KitchenChangeRequestTask {
  id: string
  title: string
  status: string
  dueAt: string | null
  assignedToProfileCode: string | null
  blocksSubjectKind: string
  blocksSubjectId: string | null
}

// ---------------------------------------------------------------------------
// Pure helpers
// ---------------------------------------------------------------------------

/** US EIN format check `\d{2}-\d{7}`. Mirror of the server-side helper. */
const EIN_REGEX = /^\d{2}-\d{7}$/
export function isValidEINFormat(value: string): boolean {
  return EIN_REGEX.test(value.trim())
}

/** Phone shape required for chef-staff invites. */
const PHONE_REGEX = /^\+?[0-9\-\s()]{6,20}$/
export function isValidChefStaffPhone(value: string): boolean {
  return PHONE_REGEX.test(value.trim())
}

/** Mask an EIN for display: `12-3456789` → `••-•••6789`. */
export function maskEin(value: string | null): string {
  if (!value) {
    return 'Not on file'
  }
  const last4 = value.slice(-4)
  return `••-•••${last4}`
}

/** Friendly role label for badges in the roster table. */
export function chefRoleLabel(role: ChefRole): string {
  return role === 'SUPPLIER_ADMIN'
    ? 'Supplier Admin'
    : role === 'SUPPLIER_STAFF'
      ? 'Supplier Staff'
      : role === 'CHEF_ADMIN'
        ? 'Chef Admin'
        : 'Chef Staff'
}

/**
 * Server enum → the lowercase mode the chef display path (`ScaleContext.mode`)
 * reads. Anything unknown (or absent) reads as the product default.
 */
export function toMeasurementDisplay(mode: MeasurementDisplayMode | null | undefined): 'standard' | 'technical' {
  return mode === 'TECHNICAL' ? 'technical' : 'standard'
}

// ---------------------------------------------------------------------------
// Fetch helpers
// ---------------------------------------------------------------------------

export async function fetchMyKitchens(signal?: AbortSignal): Promise<MyKitchensResponse> {
  return apiFetch<MyKitchensResponse>('/api/kitchen-settings/my-kitchens', { signal })
}

export async function fetchKitchenDetail(orgId: string, signal?: AbortSignal): Promise<KitchenDetailResponse> {
  return apiFetch<KitchenDetailResponse>(`/api/kitchen-settings/${encodeURIComponent(orgId)}`, { signal })
}

export async function fetchKitchenTeam(orgId: string, signal?: AbortSignal): Promise<KitchenTeamResponse> {
  return apiFetch<KitchenTeamResponse>(`/api/kitchen-settings/${encodeURIComponent(orgId)}/team`, { signal })
}

export async function fetchKitchenPayouts(orgId: string, signal?: AbortSignal): Promise<KitchenPayoutsResponse> {
  return apiFetch<KitchenPayoutsResponse>(`/api/kitchen-settings/${encodeURIComponent(orgId)}/payouts`, { signal })
}

export async function fetchKitchenMeasurementPreference(
  orgId: string,
  signal?: AbortSignal,
): Promise<KitchenMeasurementPreferenceResponse> {
  return apiFetch<KitchenMeasurementPreferenceResponse>(
    `/api/kitchen-settings/${encodeURIComponent(orgId)}/measurement-preference`,
    { signal },
  )
}

export interface UpdateKitchenMeasurementPreferenceVariables {
  organizationId: string
  displayMode: MeasurementDisplayMode
}

export async function updateKitchenMeasurementPreference(
  vars: UpdateKitchenMeasurementPreferenceVariables,
): Promise<KitchenMeasurementPreferenceResponse> {
  const { organizationId, displayMode } = vars
  return apiFetch<KitchenMeasurementPreferenceResponse>(
    `/api/kitchen-settings/${encodeURIComponent(organizationId)}/measurement-preference`,
    { method: 'PATCH', body: { displayMode } },
  )
}

export interface UpdateKitchenProfileVariables {
  organizationId: string
  logoUri?: string | null
  description?: string | null
  phoneNumber?: string | null
  timeZone?: string | null
  displayName?: string | null
  contactEmail?: string | null
}

export async function updateKitchenProfile(vars: UpdateKitchenProfileVariables): Promise<KitchenDetailResponse> {
  const { organizationId, ...body } = vars
  return apiFetch<KitchenDetailResponse>(`/api/kitchen-settings/${encodeURIComponent(organizationId)}`, {
    method: 'PATCH',
    body,
  })
}

export interface InviteChefStaffVariables {
  kitchenOrgId: string
  email: string
  name: string
  phone: string
  role: ChefRole
  locationId?: string | null
}

export interface InviteChefStaffResponse {
  invitation: KitchenPendingInvite
  existingMembershipId: string | null
}

export async function inviteChefStaff(vars: InviteChefStaffVariables): Promise<InviteChefStaffResponse> {
  const { kitchenOrgId, ...body } = vars
  return apiFetch<InviteChefStaffResponse>(`/api/kitchen-settings/${encodeURIComponent(kitchenOrgId)}/invitations`, {
    method: 'POST',
    body,
  })
}

export async function removeChefMembership(membershipId: string): Promise<{ id: string; isArchived: boolean }> {
  return apiFetch<{ id: string; isArchived: boolean }>(
    `/api/kitchen-settings/memberships/${encodeURIComponent(membershipId)}`,
    { method: 'DELETE' },
  )
}

export interface CreateKitchenChangeRequestVariables {
  kitchenOrgId: string
  field: string
  currentValue: string | null
  proposedValue: string
  reason: string
  urgency: KitchenChangeRequestUrgency
}

export async function createKitchenChangeRequest(
  vars: CreateKitchenChangeRequestVariables,
): Promise<KitchenChangeRequestTask> {
  const { kitchenOrgId, ...body } = vars
  return apiFetch<KitchenChangeRequestTask>(
    `/api/kitchen-settings/${encodeURIComponent(kitchenOrgId)}/change-requests`,
    { method: 'POST', body },
  )
}

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const myKitchensQueryKey = ['kitchen-settings', 'my-kitchens'] as const
export const kitchenDetailQueryKey = (orgId: string) => ['kitchen-settings', 'detail', orgId] as const
export const kitchenTeamQueryKey = (orgId: string) => ['kitchen-settings', 'team', orgId] as const
export const kitchenPayoutsQueryKey = (orgId: string) => ['kitchen-settings', 'payouts', orgId] as const
export const kitchenMeasurementPreferenceQueryKey = (orgId: string) =>
  ['kitchen-settings', 'measurement-preference', orgId] as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useMyKitchensQuery() {
  return useQuery<MyKitchensResponse>({
    queryKey: myKitchensQueryKey,
    queryFn: ({ signal }) => fetchMyKitchens(signal),
    staleTime: 30_000,
  })
}

export function useKitchenDetailQuery(orgId: string | null) {
  return useQuery<KitchenDetailResponse>({
    queryKey: kitchenDetailQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => fetchKitchenDetail(orgId!, signal),
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useKitchenTeamQuery(orgId: string | null) {
  return useQuery<KitchenTeamResponse>({
    queryKey: kitchenTeamQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => fetchKitchenTeam(orgId!, signal),
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useKitchenPayoutsQuery(orgId: string | null) {
  return useQuery<KitchenPayoutsResponse>({
    queryKey: kitchenPayoutsQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => fetchKitchenPayouts(orgId!, signal),
    enabled: !!orgId,
    staleTime: 30_000,
  })
}

export function useKitchenMeasurementPreferenceQuery(orgId: string | null) {
  return useQuery<KitchenMeasurementPreferenceResponse>({
    queryKey: kitchenMeasurementPreferenceQueryKey(orgId ?? ''),
    queryFn: ({ signal }) => fetchKitchenMeasurementPreference(orgId!, signal),
    enabled: !!orgId,
    staleTime: 60_000,
  })
}

export function useUpdateKitchenMeasurementPreferenceMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateKitchenMeasurementPreference,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: kitchenMeasurementPreferenceQueryKey(vars.organizationId) })
    },
  })
}

export function useUpdateKitchenProfileMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: updateKitchenProfile,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: kitchenDetailQueryKey(vars.organizationId) })
      void qc.invalidateQueries({ queryKey: myKitchensQueryKey })
    },
  })
}

export function useInviteChefStaffMutation() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: inviteChefStaff,
    onSuccess: (_data, vars) => {
      void qc.invalidateQueries({ queryKey: kitchenTeamQueryKey(vars.kitchenOrgId) })
    },
  })
}

export function useRemoveChefMembershipMutation(orgId: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: removeChefMembership,
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: kitchenTeamQueryKey(orgId) })
    },
  })
}

export function useCreateKitchenChangeRequestMutation() {
  return useMutation({
    mutationFn: createKitchenChangeRequest,
  })
}
